import { create } from "zustand";
import { queryClient } from "@/app/queryClient";

export type Role =
  | "technical_admin"
  | "super_admin"
  | "operations"
  | "hr"
  | "attendance_manager"
  | "admin"
  | "employee"
  | "client";

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  role: Role;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  // Absolute session cap (ISO 8601) returned by the server. The session-
  // expiry pill anchors to this value, NOT to the access-token `exp`, so
  // silent rotation does not reset the user-facing countdown.
  sessionExpiresAt: string | null;
  permissions: Set<string>;
  authVersion: number;
  hydrated: boolean;
  // Explicit signal set only by forceLogout (refresh failed / token reused /
  // explicit logout). SessionExpiredModal reads this; cleared on next setAuth.
  sessionExpired: boolean;
  // Transient (not persisted). Set while a self-initiated password change is
  // in flight: the server bumps auth_version and pushes auth.force_logout
  // over the WebSocket, which would hard-redirect to /login before the user
  // sees any prompt. ForceLogoutListener + the 401 interceptor skip their
  // auto-logout while this is set — the initiating flow owns the sign-out.
  suppressForceLogout: boolean;
  setSuppressForceLogout: (v: boolean) => void;
  setAuth: (data: {
    user: AuthUser;
    accessToken: string;
    permissions: string[];
    authVersion: number;
    sessionExpiresAt?: string | null;
  }) => void;
  setAccessToken: (token: string, sessionExpiresAt?: string | null) => void;
  setSessionExpiresAt: (iso: string | null) => void;
  setHydrated: (v: boolean) => void;
  // reason="expired" → SessionExpiredModal fires.
  // reason="user"    → clean intentional sign-out, no modal.
  forceLogout: (reason?: "expired" | "user") => void;
}

const STORAGE_KEY = "workman.auth";

interface Persisted {
  user: AuthUser;
  accessToken: string;
  sessionExpiresAt: string | null;
  permissions: string[];
  authVersion: number;
}

function loadPersisted(): Persisted | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    if (!parsed.user || !parsed.accessToken) return null;
    return {
      user: parsed.user,
      accessToken: parsed.accessToken,
      sessionExpiresAt: parsed.sessionExpiresAt ?? null,
      permissions: parsed.permissions ?? [],
      authVersion: parsed.authVersion ?? 0,
    };
  } catch {
    return null;
  }
}

function persist(state: {
  user: AuthUser | null;
  accessToken: string | null;
  sessionExpiresAt: string | null;
  permissions: Set<string>;
  authVersion: number;
}) {
  try {
    if (state.user && state.accessToken) {
      const data: Persisted = {
        user: state.user,
        accessToken: state.accessToken,
        sessionExpiresAt: state.sessionExpiresAt,
        permissions: Array.from(state.permissions),
        authVersion: state.authVersion,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

const initial = loadPersisted();

export const useAuthStore = create<AuthState>((set, get) => ({
  user: initial?.user ?? null,
  accessToken: initial?.accessToken ?? null,
  sessionExpiresAt: initial?.sessionExpiresAt ?? null,
  permissions: new Set(initial?.permissions ?? []),
  authVersion: initial?.authVersion ?? 0,
  hydrated: true,
  sessionExpired: false,
  suppressForceLogout: false,
  setSuppressForceLogout: (v) => set({ suppressForceLogout: v }),
  setAuth: ({ user, accessToken, permissions, authVersion, sessionExpiresAt }) => {
    const prevUserId = get().user?.id;
    const permSet = new Set(permissions);
    if (prevUserId && prevUserId !== user.id) {
      queryClient.clear();
    }
    const nextExpiry = sessionExpiresAt ?? get().sessionExpiresAt;
    persist({
      user,
      accessToken,
      sessionExpiresAt: nextExpiry,
      permissions: permSet,
      authVersion,
    });
    set({
      user,
      accessToken,
      permissions: permSet,
      authVersion,
      sessionExpiresAt: nextExpiry,
      hydrated: true,
      sessionExpired: false,
    });
  },
  setAccessToken: (token, sessionExpiresAt) =>
    set((s) => {
      const nextExpiry =
        sessionExpiresAt === undefined ? s.sessionExpiresAt : sessionExpiresAt;
      if (s.user) {
        persist({
          user: s.user,
          accessToken: token,
          sessionExpiresAt: nextExpiry,
          permissions: s.permissions,
          authVersion: s.authVersion,
        });
      }
      return { accessToken: token, sessionExpiresAt: nextExpiry };
    }),
  setSessionExpiresAt: (iso) =>
    set((s) => {
      if (s.user && s.accessToken) {
        persist({
          user: s.user,
          accessToken: s.accessToken,
          sessionExpiresAt: iso,
          permissions: s.permissions,
          authVersion: s.authVersion,
        });
      }
      return { sessionExpiresAt: iso };
    }),
  setHydrated: (v) => set({ hydrated: v }),
  forceLogout: (reason: "expired" | "user" = "expired") => {
    const hadUser = !!get().user;
    queryClient.cancelQueries();
    queryClient.clear();
    persist({
      user: null,
      accessToken: null,
      sessionExpiresAt: null,
      permissions: new Set(),
      authVersion: 0,
    });
    set({
      user: null,
      accessToken: null,
      sessionExpiresAt: null,
      permissions: new Set(),
      authVersion: 0,
      hydrated: true,
      sessionExpired: reason === "expired" && hadUser,
    });
  },
}));
